import React, { useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Download } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/config/api";
import { downloadCsv } from "@/lib/exportCsv";

/** Shows the records behind a chart element. `request` is the /bi/drill-down body. */
export const DrillDownDialog = ({ open, onOpenChange, title, request }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open || !request) return;
    setLoading(true);
    axios
      .post(`${API}/bi/drill-down`, request, { withCredentials: true })
      .then(({ data }) => setResult(data))
      .catch((error) => {
        console.error("Drill-down failed", error);
        toast.error(error?.response?.data?.detail || "Could not load the underlying records");
      })
      .finally(() => setLoading(false));
  }, [open, request]);

  const columns = (result?.columns || []).filter((column) =>
    (result?.records || []).some((record) => record[column] !== null && record[column] !== undefined && record[column] !== "")
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl bg-white dark:bg-slate-900 max-h-[85vh] overflow-y-auto"
        data-testid="drill-down-dialog"
      >
        <DialogHeader>
          <DialogTitle>{title || "Underlying records"}</DialogTitle>
          <DialogDescription>
            {loading ? "Loading…" : `${result?.count ?? 0} record(s) behind this number`}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
          </div>
        ) : !result?.records?.length ? (
          <p className="text-sm text-muted-foreground">No records matched.</p>
        ) : (
          <>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 w-fit"
              onClick={() =>
                downloadCsv(
                  `drill-down-${result.collection}`,
                  columns,
                  result.records.map((record) => columns.map((column) => record[column] ?? ""))
                )
              }
              data-testid="drill-down-export-btn"
            >
              <Download className="h-4 w-4" />
              Export these records
            </Button>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b">
                    {columns.map((column) => (
                      <th key={column} className="text-left py-2 px-2 font-semibold whitespace-nowrap">
                        {column.replace(/_/g, " ")}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.records.map((record, index) => (
                    <tr key={index} className="border-b" data-testid={`drill-down-row-${index}`}>
                      {columns.map((column) => (
                        <td key={column} className="py-1.5 px-2 whitespace-nowrap">
                          {record[column] === null || record[column] === undefined || record[column] === ""
                            ? "—"
                            : String(record[column])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DrillDownDialog;
